'use client'

import WorldSettingPage, { WorldSettingsDefaultValues } from '@/components/WorldSettingPage'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useRoomPolicy } from '@/hooks/useRoomPolicy'
import { extractIdFromImageUrl } from '@/lib/asset'
import { CreateRoomData, fetchTrailerMedia, patchRoom, Room } from '@/services/cms'
import { Policy } from '@/types/world'
import { PropsWithChildren } from 'react'
import useSWR from 'swr'

type WorldSettingDialogProps = PropsWithChildren<{
  info: Partial<Room>
  open: boolean
  onOpenChange: (open: boolean) => void
  onAfterMutatingWorld?: (deleteRoomId?: string) => void
}>

export default function ControlledWorldSettingDialog({
  info,
  open,
  onOpenChange,
  onAfterMutatingWorld,
}: WorldSettingDialogProps) {
  const { store } = useConfigsContext()
  const ROOM_POLICY = useRoomPolicy()
  const htcToken = store?.credentials?.htcToken
  const matchedPolicy = Object.entries(ROOM_POLICY).find(([, { value }]) => value === info.policy)

  const { data: trailerMedia, isLoading: isLoadingTrailerMedia } = useSWR(
    open ? `fetchTrailerMedia-${info.id}` : null,
    () =>
      fetchTrailerMedia(info.id!).then((res) => {
        return res?.results
      }),
  )

  const worldDefaultValues: WorldSettingsDefaultValues = {
    title: info.title || '',
    description: info.description_plaintext || '',
    policy: (matchedPolicy ? matchedPolicy[0] : 'private') as Policy,
    passcode: '',
    genre: info.tags,
    enablePasscode: !!info.is_protected,
    preferredDevices: info.preferred_devices || [],
    isAdultOnly: info.is_adult_only || false,
    logoOverlayPosition: info.logo_overlay_position || 'top-left',
    githubUrl: info.github_url || '',
  }

  const worldDefaultThumbnail = {
    id: extractIdFromImageUrl(info.image),
    url: info.image || '',
  }

  const handleWorldSettingsComplete = async (data: CreateRoomData) => {
    if (!htcToken) throw new Error('missing htcToken')
    if (!info?.id) throw new Error('missing room id')

    const res = await patchRoom(htcToken, info.id, data)

    if (res?.status === 200) {
      onOpenChange(false)
      onAfterMutatingWorld?.()
    }
  }

  return (
    <>
      {open && (
        <WorldSettingPage
          defaultValues={worldDefaultValues}
          defaultThumbnail={worldDefaultThumbnail}
          onCompleteRoomUpdate={handleWorldSettingsComplete}
          mode="update"
          hubSid={info.hub_sid}
          externalData={info}
          roomId={info.id}
          owner={info.owner}
          trailerMedia={trailerMedia}
          isLoadingTrailerMedia={isLoadingTrailerMedia}
          close={() => onOpenChange(false)}
        />
      )}
    </>
  )
}
